<template>
  <div>
    <div class="fake-input" @click="openDialog('add')">
      <el-button class="input-value-add el-button--primary is-medium">
        {{ $t('add') }}
        <KeyenceHTHardKeyButton
          v-if="isKeyenceEnterpriseBrowser"
          :hardkey="KeyenceHardKey.NUM_1"
          :function="() => openDialog('add')"
        />
      </el-button>
    </div>

    <div class="barcode-number-block py-2">
      <span>
        {{ $t('number_of_sent_items') }}
      </span>
      <span class="label-box"> {{ sentItemCount }}</span>
    </div>
    <div class="barcode-number-block py-2 new-scanned-block" :class="{ 'has-items': scannedItemCount > 0 }">
      <span>
        {{ $t('number_of_scanned_items') }}
      </span>
      <span class="label-box new-scanned-count"> {{ scannedItemCount }}</span>
    </div>

    <div class="fake-input" @click="openDialog('remove')">
      <el-button
        :disabled="listValue.length === 0"
        type="danger"
        class="input-value el-button--primary is-medium delete-button"
      >
        {{ $t('remove') }}
        <KeyenceHTHardKeyButton
          v-if="isKeyenceEnterpriseBrowser"
          :hardkey="KeyenceHardKey.NUM_2"
          :function="() => openDialog('remove')"
        />
      </el-button>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :width="'100%'"
      :fullscreen="true"
      :destroy-on-close="true"
      :lock-scroll="true"
      :close-on-click-modal="false"
      :show-close="false"
      :custom-class="dialogModeClass"
    >
      <template #title>
        <div class="flex flex-col justify-center items-center gap-2">
          <div v-if="screenNameKey" class="screen-name-label">{{ $t(screenNameKey) }}</div>
          <div class="label-round" style="display: block;">{{ $t('list_scanned_items') }}</div>
        </div>
      </template>
      <div class="edit-view" v-loading.fullscreen="loading">
        <div
          class="overflow-y-auto border-2 border-solid border-gray-300 rounded-lg px-2 pb-4 list-container"
          ref="listContainer"
        >
          <div v-if="listValue.length > 0">
            <div v-for="(item, index) in listValue" :key="index" class="barcode-item-wrapper">
              <div class="barcode-item">
                <div class="trolley-info">
                  <span v-if="item.trollyNumber" class="trolley-number" :title="$t('trolley_number')">
                    <IconTrolley />
                    {{ item.trollyNumber }}
                  </span>
                  <span class="trolley-index" :title="$t('trolley_index')">#{{ item.trollyIndex }}</span>
                </div>

                <span class="item-text">
                  <div class="item-code">{{ item.code }}</div>
                  <!-- <div class="item-name" v-if="item.itemName">{{ item.itemName }}</div> -->
                </span>

                <div class="amount-input-wrapper" @input="onAmountInput(index, $event)">
                  <el-input-number
                    v-model="item.amount"
                    :disabled="!isItemEditable(index)"
                    :min="0.0"
                    :step="0.1"
                    :max="item.limitAmount || Infinity"
                    :controls="false"
                    @change="updateItemAmount(index, $event)"
                    @keydown.enter.prevent
                    class="amount-input"
                  />
                  <span class="unit-suffix">{{ item.unit.toUpperCase() }}</span>
                </div>
                <div class="item-name" v-if="item.itemName">{{ item.itemName }}</div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            {{ $t('no_items_to_display') }}
          </div>
        </div>

        <!-- Manual input section when not in scan mode -->
        <div v-if="!isScan" class="manual-input-section">
          <div class="input-row">
            <el-button
              type="primary"
              @click="addItem"
              class="add-btn el-button--primary is-medium"
              :disabled="!inputValue.trim() || wouldExceedMaxLength"
            >
              {{ $t('ok') }}
              <KeyenceHTHardKeyButton
                v-if="isKeyenceEnterpriseBrowser"
                :hardkey="KeyenceHardKey.ENTER"
                :function="addItem"
                :visible="dialogVisible && !isScan"
                :disabled="!inputValue.trim() || wouldExceedMaxLength"
                :newLayer="true"
              />
            </el-button>
          </div>
        </div>

        <div v-else-if="isKeyenceEnterpriseBrowser" class="flex flex-col items-center justify-center">
          <KeyenceHTListStringButton
            ref="keyenceScanButton"
            v-model:loading="loading"
            :scanMode="scanMode"
            @update:confirm="onScanConfirm"
          />
          <KeyenceHTScannerConfig :scannerConfigName="'ListStringScan'" :shouldBulkMode="true" />
        </div>
        <div v-else class="scan-mode-buttons">
          <el-button type="primary" @click="openScanDialog()" class="scan-add-btn" size="large">
            <div class="button-content">
              <span class="button-text">
                {{ $t('scan_items') }}
              </span>
            </div>
          </el-button>
        </div>

        <div class="barcode-number-block py-1">
          <span>
            {{ $t('number_of_sent_items') }}
          </span>
          <span class="label-box">
            {{ sentItemCount }}
          </span>
        </div>
        <div class="barcode-number-block py-1 new-scanned-block" :class="{ 'has-items': scannedItemCount > 0 }">
          <span>
            {{ $t('number_of_scanned_items') }}
          </span>
          <span class="label-box new-scanned-count">
            {{ scannedItemCount }}
          </span>
        </div>

        <!-- Summary Section -->
        <div class="summary-section" v-show="isOperatorTypeAdd">
          <div class="summary-item">
            <span class="summary-label">{{ $t('produced_serial_total_number') }}</span>
            <span class="summary-count">{{ producedSerialTotalNumber }} {{ $t('items') }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ $t('total') }}</span>
            <span class="summary-count">{{ currentItemCount }} / {{ maxItemCount }} {{ $t('items') }}</span>
          </div>
        </div>
        <div class="dialog-actions">
          <!-- <el-button @click="cancelChanges" class="cancel-btn el-button--primary is-medium">
            {{ $t('cancel') }}
          </el-button> -->
          <el-button type="primary" @click="confirmChanges" class="confirm-btn el-button--primary is-medium">
            {{ $t('confirm') }}
            <KeyenceHTHardKeyButton
              v-if="isKeyenceEnterpriseBrowser"
              :hardkey="KeyenceHardKey.ENTER"
              :function="confirmChanges"
              :newLayer="true"
              :visible="dialogVisible"
            />
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Scan Mode Dialog -->
    <ListStringScanComponent
      v-if="scanDialogVisible"
      v-model="scanDialogVisible"
      :scanned-values="localValue"
      :min-value="minLength"
      :max-value="maxLength"
      :scanMode="scanMode"
      :operator-type="operatorType"
      @update:confirm="onScanConfirm"
      @confirm:listSerialCodes="onConfirmListSerialCodes"
    />

    <ConfirmDialog
      v-model="isConfirmDeleteDialogVisible"
      :title="$t('confirm_delete_title')"
      :message="$t('confirm_delete_item', { code: deleteTargetCode })"
      :confirmText="$t('delete')"
      :cancelText="$t('cancel')"
      confirmType="primary"
      :confirmHardKey="KeyenceHardKey.ENTER"
      @confirm="onConfirmDelete"
      @cancel="onCancelDelete"
    />
  </div>
</template>

<script lang="ts">
import { ISerialHistoryFullDataItem } from '@/apis/serialAPI'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import KeyenceHTListStringButton from '@/components/keyenceHT/ListStringScanButton.vue'
import KeyenceHTScannerConfig from '@/components/keyenceHT/ScannerConfig.vue'
import ListStringScanComponent from '@/components/ListStringScanComponent.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import IconTrolley from '@/components/svg/IconTrolley.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import {
  CHECK_SERIAL_DETAIL,
  CHECK_SERIAL_LAST_HISTORY_DETAIL,
  GET_SERIAL_HISTORIES,
  OPEN_DIALOG,
} from '@/store/actions'
import {
  ALLOWED_SERIAL_PREFIX,
  DATE_TIME,
  getSerialErrorMessage,
  LEFT_AMOUNT_START_TROLLEY_INDEX,
  POSITIVE_DECIMAL_REGEX,
  DEFAULT_DEBOUNCE_TIME,
  UNIT_OPTIONS,
} from '@/utils/constants'
import { EOperatorType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { logError } from '@/utils/errorLogger'
import { openMessage, trollyNumberLimit } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { unsubmittedSerialCodes } from '@/utils/serialChanges'
import { IListStringItem, TError } from '@/utils/types/commonTypes'
import { format } from 'date-fns'
import debounce from 'lodash/debounce'
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeSymbologiesSelection,
    ConfirmDialog,
    SelectionCameraPopup,
    IconSwitch,
    IconTrolley,
    ScanArea,
    ListStringScanComponent,
    KeyenceHTListStringButton,
    KeyenceHTScannerConfig,
    KeyenceHTHardKeyButton,
  },
  emits: ['update:modelValue', 'change', 'removedListString'],
  inheritAttrs: false,
  name: 'InputListString',
})
export default class InputListString extends Vue {
  @Prop({ type: Array, default: () => [] }) modelValue?: IListStringItem[]
  @Prop({ type: Boolean, default: false }) isScan?: boolean
  @Prop({ type: String, default: '' }) fieldSchema?: string
  @Prop({ type: Number }) readonly minLength?: number
  @Prop({ type: Number }) readonly maxLength?: number
  @Prop({ type: String }) readonly fieldName?: string
  @Prop({ type: String }) readonly error?: string
  @Prop({ type: String }) readonly operatorType?: EOperatorType

  dialogVisible = false
  loading = false
  localValue: IListStringItem[] = []
  deletedSerialData: IListStringItem[] = []

  inputValue = ''

  // Scan mode state
  scanDialogVisible = false
  scanLoading = false
  manualScanValue = ''

  // Camera and barcode scanner state
  isReadyToScan = true
  scannedDialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IListStringItem[] = []
  isConfirmDeleteDialogVisible = false
  isHandlingAddScan = false
  deleteTargetCode = ''

  // Scan mode state
  scanMode: 'add' | 'remove' = 'add'

  KeyenceHardKey = KeyenceHardKey

  mounted() {
    this.localValue = [...(this.modelValue || [])]
  }

  get forms() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.trackPointForms
  }

  get project() {
    return this.$store.state.project.details
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentManufacturingPlan() {
    return this.$store.state.barcode.manufacturingPlan
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get listValue(): IListStringItem[] {
    return this.localValue || []
  }

  get isOperatorTypeUse(): boolean {
    return this.operatorType === EOperatorType.USE
  }

  get isOperatorTypeAdd(): boolean {
    return this.operatorType === EOperatorType.ADD
  }

  get screenNameKey(): string {
    switch (this.operatorType) {
      case EOperatorType.ADD:
        return 'operator_screen_produce'
      case EOperatorType.USE:
        return 'operator_screen_use'
      case EOperatorType.CORRECT_VALUE:
        return 'operator_screen_left_amount'
      default:
        return ''
    }
  }

  get dialogModeClass(): string {
    switch (this.operatorType) {
      case EOperatorType.ADD:
        return 'mode-operator-add'
      case EOperatorType.USE:
        return 'mode-operator-use'
      case EOperatorType.CORRECT_VALUE:
        return 'mode-left-amount-collection'
      default:
        return ''
    }
  }

  get isOperatorTypeCollect(): boolean {
    return this.operatorType === EOperatorType.CORRECT_VALUE
  }

  get validationError(): string {
    const length = this.listValue.length

    if (this.error) {
      return this.error
    }

    if (this.minLength !== undefined && length < this.minLength) {
      return this.$t('minimum_items_required', { minLength: this.minLength })
    }

    if (this.maxLength !== undefined && length > this.maxLength) {
      return this.$t('maximum_items_required', { maxLength: this.maxLength })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  get wouldExceedMaxLength(): boolean {
    return this.maxLength !== undefined && this.localValue.length >= this.maxLength
  }

  get totalAmount(): number {
    return this.listValue.reduce((sum, item) => sum + (item.amount || 0), 0)
  }

  get currentItemCount(): number {
    return this.listValue.length || 0
  }

  get sentSerialList(): IListStringItem[] {
    const fieldKey = (this.$attrs.name as string) || 'sirial'
    return (this.previousTrackingData?.customFields?.[fieldKey]?.listString as IListStringItem[]) || []
  }

  get sentItemCount(): number {
    return this.sentSerialList.length
  }

  get scannedItemCount(): number {
    return unsubmittedSerialCodes(this.sentSerialList, this.listValue, this.deletedSerialData).length
  }

  get producedSerialTotalNumber(): number {
    return this.barcode?.serialSetting.producedSerialTotalNumber || 0
  }

  get maxItemCount(): number {
    return this.barcode?.serialSetting.producedSerialMaximumTotalNumber || 0
  }

  get defaultUnit(): string {
    return this.unitOptions[0]?.value || ''
  }

  get stockUnit(): string {
    return this.barcode?.serialSetting.stockUnit
  }

  get defaultAmount(): number {
    return 0
  }

  get defaultLimitAmount(): number {
    return 10
  }

  @Emit('update:modelValue')
  updateModelValue(newList: IListStringItem[]) {
    return newList
  }

  @Emit('change')
  emitChange(newList: IListStringItem[]) {
    return newList
  }

  @Emit('removedListString')
  emitRemovedListString(removed: IListStringItem[]) {
    return removed
  }

  get unitOptions() {
    return UNIT_OPTIONS.map((unit) => ({ label: unit, value: unit }))
  }

  addItem(e: Event) {
    e.preventDefault()
    if (this.inputValue.trim() && !this.wouldExceedMaxLength) {
      const code = this.inputValue.trim()
      if (!this.validateCodePrefix(code)) {
        const invalidCodeLabel = this.$t('invalid_code_prefix')
        openMessage(invalidCodeLabel, 'error')
        logError('', {}, '', invalidCodeLabel)
        return
      }
      const newItem = this.createNewSerialItem({ code })
      this.removeFromDeletedSerialData(newItem)
      this.localValue = [...this.localValue, newItem]
      this.inputValue = ''
    }
  }

  private removeFromDeletedSerialData(item: IListStringItem): void {
    const index = this.deletedSerialData.findIndex(
      (deleted) => deleted.code === item.code && deleted.id === item.id
    )
    if (index !== -1) {
      this.deletedSerialData.splice(index, 1)
    }
  }

  removeItem(index: number) {
    this.localValue = this.localValue.filter((_, i) => i !== index)
  }

  updateItemAmount(index: number, value: string | number) {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value
    if (!isNaN(numericValue) && numericValue >= 0) {
      this.localValue[index].amount = numericValue
    }
  }

  created() {
    this.warnAmountInput = debounce(this.warnAmountInput.bind(this), DEFAULT_DEBOUNCE_TIME)
  }

  warnAmountInput(index: number, raw: string) {
    const item = this.localValue[index]
    if (!item || !raw) return

    if (!POSITIVE_DECIMAL_REGEX.test(raw.trim())) {
      openMessage(this.$t('amount_invalid_format'), 'warning')
      return
    }

    const parsed = parseFloat(raw)
    if (item.limitAmount !== undefined && parsed > item.limitAmount) {
      openMessage(
        this.$t('amount_exceeds_limit', {
          limit: item.limitAmount,
          unit: (item.unit || '').toUpperCase(),
        }),
        'warning'
      )
    }
  }

  onAmountInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement | null
    if (input?.tagName === 'INPUT') {
      this.warnAmountInput(index, input.value)
    }
  }

  confirmChanges() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
    this.emitRemovedListString(this.deletedSerialData)

    this.dialogVisible = false
  }

  async cancelChanges() {
    const hasChanges = this.hasUnsavedChanges()

    if (hasChanges) {
      const changesSummary = this.getChangesSummary()

      try {
        await this.$confirm(
          this.$t('unsaved_changes', { changes: changesSummary }),
          this.$t('title_unsaved_changes', { changes: changesSummary }),
          {
            confirmButtonText: this.$t('discard_changes'),
            cancelButtonText: this.$t('keep_editing'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
      } catch (error) {}
    }
    this.resetToOriginalValues()
  }

  private hasUnsavedChanges(): boolean {
    return JSON.stringify(this.localValue) !== JSON.stringify(this.modelValue || [])
  }

  private getChangesSummary(): string {
    const originalLength = this.modelValue?.length || 0
    const currentLength = this.localValue.length

    if (originalLength !== currentLength) {
      return ` (${originalLength} → ${currentLength} items)`
    }
    return ` (${currentLength} items modified)`
  }

  private resetToOriginalValues() {
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = false
  }

  openDialog(mode: 'add' | 'remove') {
    this.scanMode = mode
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = true

    if (this.localValue.length > 0) {
      this.scrollToBottom()
    }
  }

  private scrollToBottom() {
    this.$nextTick(() => {
      const container = this.$refs.listContainer as HTMLElement
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })
  }

  // Scan mode methods
  openScanDialog() {
    this.scannedBarcodeList = []
    this.manualScanValue = ''
    this.scanDialogVisible = true
  }

  async onScanConfirm(scannedValue: string) {
    if (!scannedValue.trim() || this.isScanBlocked) return

    const trimmedScannedValue = scannedValue.trim()

    if (this.scanMode === 'add') {
      await this.handleScanAdd(trimmedScannedValue)
    } else {
      await this.handleScanRemove(trimmedScannedValue)
    }

    this.onConfirmListSerialCodes()
  }

  private validateScanAdd(scannedValue: string): boolean {
    if (this.wouldExceedMaxLength) {
      this.isHandlingAddScan = false
      const maximumItemLabel = this.$t('maximum_items_reached', { maxItems: this.maxLength })
      openMessage(maximumItemLabel, 'warning')
      logError('', {}, '', maximumItemLabel)
      return false
    }

    if (!this.isOperatorTypeUse && !this.validateCodePrefix(scannedValue)) {
      this.isHandlingAddScan = false
      logError('', {}, '', this.$t('invalid_code_prefix'))
      this.handleSerialPrefixError(scannedValue)
      return false
    }

    if (scannedValue.toLowerCase().startsWith('false')) {
      this.isHandlingAddScan = false
      // openMessage(this.$t('invalid_code_prefix'), 'warning')
      return false
    }

    const existingItem = this.localValue.find((item) => item.code === scannedValue)
    if (existingItem && this.isOperatorTypeAdd) {
      openMessage(this.$t('item_already_scanned'), 'warning')
      this.isHandlingAddScan = false
      return false
    }

    return true
  }

  private async handleScanAdd(scannedValue: string) {
    this.isHandlingAddScan = true

    if (!this.validateScanAdd(scannedValue)) return

    if (this.operatorType) {
      const validatedData = await this.validateSerialCode(scannedValue, this.operatorType)
      if (!validatedData) return
      this.addSerialItemToList(validatedData)
    }
    const item = this.localValue.find((item) => item.code === scannedValue)
    const index = this.localValue.findIndex((item) => item.code === scannedValue)
    const content = this.formatItemContent(item)
    this.isHandlingAddScan = false
    if (item?.warnings && item.warnings.length > 0) return
    openMessage(
      this.$t('item_detected', {
        index: index + 1,
        item: content,
      }),
      'success'
    )
  }

  private formatItemContent(item: IListStringItem | undefined): string {
    if (!item) return ''
    const parts = [item.code]
    if (item.itemCode) {
      parts.push(item.itemCode)
    }
    if (item.lot) {
      parts.push(`LOT: ${item.lot}`)
    }
    return parts.join(' - ')
  }

  private async handleScanRemove(scannedValue: string) {
    const reversedIndex = this.localValue
      .slice()
      .reverse()
      .findIndex((item) => item.code === scannedValue)

    if (reversedIndex === -1) return

    this.deleteTargetCode = scannedValue
    this.isConfirmDeleteDialogVisible = true
  }

  private onConfirmDelete() {
    const reversedIndex = this.localValue
      .slice()
      .reverse()
      .findIndex((item) => item.code === this.deleteTargetCode)

    if (reversedIndex === -1) {
      this.deleteTargetCode = ''
      return
    }

    const index = this.localValue.length - 1 - reversedIndex
    const deletedItem = { ...this.localValue[index], scannedDateTime: new Date().toISOString() }
    this.deletedSerialData.push(deletedItem)
    this.localValue.splice(index, 1)
    this.updateTrolleyIndexes()
    openMessage(this.$t('item_deleted'), 'success')
    this.deleteTargetCode = ''
  }

  private onCancelDelete() {
    this.deleteTargetCode = ''
  }

  private updateTrolleyIndexes() {
    if (!this.operatorType) return

    this.localValue.forEach((item, index) => {
      if (this.operatorType === EOperatorType.CORRECT_VALUE) {
        item.trollyIndex = LEFT_AMOUNT_START_TROLLEY_INDEX + index
        delete item.trollyNumber
      } else {
        const newTrolleyIndex = index + 1
        item.trollyIndex = newTrolleyIndex
        item.trollyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.localValue))
      }
    })
  }

  get previousTrackingData() {
    return this.$store.getters.trackingData
  }

  private isAllowDuplicatedSerial(serial: string): boolean {
    if (!this.localValue) {
      return false
    }

    return !this.localValue.some((item) => item.code === serial && item.producedSerialHistoryId)
  }

  private async validateSerialCode(code: string, operatorType: EOperatorType): Promise<IListStringItem | null> {
    try {
      this.loading = true
      const data: IListStringItem = await this.$store.dispatch(CHECK_SERIAL_DETAIL, {
        operatorType: operatorType,
        code,
        isEdit: !!this.previousTrackingData,
      })

      if (!this.isOperatorTypeAdd && !this.isAllowDuplicatedSerial(data.code)) {
        const duplicatedSerialError = this.$t('item_already_scanned')
        openMessage(duplicatedSerialError, 'warning')
        logError('', {}, '', duplicatedSerialError)

        return null
      }

      return data
    } catch (error) {
      if (this.isOperatorTypeUse && (error === '3110' || error === '3106')) {
        this.showManufacturingResultNotRegisteredError(code)
        return null
      }
      if (error === '3106' && (await this.handleAmountInsufficientError(code))) {
        return null
      }

      if (error === '3114') {
        await this.handleSerialUsageError(code)
        return null
      }

      if (error === '3123') {
        await this.handleExpirationError(code)
        return null
      }

      if (this.handleExpiredProductError(error)) {
        return null
      }
      errorHandler(error as TError)
      return null
    } finally {
      this.loading = false
      this.isHandlingAddScan = false
    }
  }

  get visibleErrorDialog() {
    return this.$store.state.dialogVisible
  }

  get isScanBlocked() {
    return this.isHandlingAddScan || this.isConfirmDeleteDialogVisible || this.visibleErrorDialog
  }

  private handleExpiredProductError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
      return false
    }

    const errorObj = error as Record<string, string[]>
    const errorKey = Object.keys(errorObj)[0]
    const errorCodes = errorObj[errorKey]

    if (!errorCodes) {
      return false
    }

    if (this.isOperatorTypeUse && (errorCodes.includes('3110') || errorCodes.includes('3106'))) {
      this.showManufacturingResultNotRegisteredError(errorKey.charAt(0).toUpperCase() + errorKey.slice(1))
      return true
    }

    const errorCode = errorCodes.find((code) => getSerialErrorMessage(code, errorKey))
    if (!errorCode) {
      return false
    }

    this.$store.dispatch(OPEN_DIALOG, {
      title: this.$t('el.messagebox.error'),
      message: getSerialErrorMessage(errorCode, errorKey),
    })

    return true
  }

  private async handleAmountInsufficientError(code: string): Promise<boolean> {
    try {
      const {
        serialHistories,
      }: { serialHistories: ISerialHistoryFullDataItem[] } = await this.$store.dispatch(GET_SERIAL_HISTORIES, { code })
      const currentManufacturingPlanId = this.currentManufacturingPlan?.id
      const useHistories = (serialHistories || []).filter((history) => history.operatorType === EOperatorType.USE)

      if (useHistories.some((history) => history.manufacturingPlanId === currentManufacturingPlanId)) {
        const duplicatedSerialError = this.$t('serial_already_registered_as_tracking_data')
        openMessage(duplicatedSerialError, 'warning')
        logError('', {}, '', duplicatedSerialError)
        return true
      }

      if (useHistories.some((history) => history.manufacturingPlanId !== currentManufacturingPlanId)) {
        const usedInAnotherOrderError = this.$t('serial_used_in_another_order')
        openMessage(usedInAnotherOrderError, 'warning')
        logError('', {}, '', usedInAnotherOrderError)
        return true
      }
    } catch (error) {
      errorHandler(error as TError)
      return true
    }

    return false
  }

  private showManufacturingResultNotRegisteredError(code: string): void {
    const notRegisteredError = this.$t('manufacturing_result_not_registered')
    const errorDetail = `<div style="font-weight: bold; text-align: center;">
      <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
        ${this.$t('usage_prohibited')}
      </div>
      <div style="color: red;">
        <div>${notRegisteredError}</div>
        <div style="margin-top: 20px;">(${code})</div>
      </div>
    </div>`

    this.$store.dispatch(OPEN_DIALOG, {
      title: this.$t('confirm_usage_item'),
      message: errorDetail,
    })
    logError('', {}, '', notRegisteredError + code)
  }

  private async handleSerialUsageError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.currentManufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${serialItemCode}</div>
          <div>${serialItemName}</div>
          <div>(${code})</div>
          <div>${this.$t('serial_already_input_error')}</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private async handleSerialPrefixError(code: string): Promise<boolean> {
    try {
      const { itemName, itemCode } = this.currentManufacturingPlan
      const requiredPrefix = itemCode?.startsWith('4') ? ALLOWED_SERIAL_PREFIX.BT : ALLOWED_SERIAL_PREFIX.TP
      const prefixMessage = this.$t('no_registered_data_for_qr', { prefix: requiredPrefix })

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${prefixMessage}</div>
          <div style="margin-top: 20px;">(${code})</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private async handleExpirationError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.currentManufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode, expiredDateTime } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${this.$t('qr_expired_cannot_use_for_manufacturing')}</div>
          <div style="margin-top: 20px;">${serialItemCode} : ${serialItemName}</div>
          <div style="margin-top: 20px;"">${this.$t('expiration_date')} : ${format(
        new Date(expiredDateTime),
        DATE_TIME.DISPLAY_FORMAT
      )} </div>
          <div style="margin-top: 20px;">(${code})</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private addSerialItemToList(data: Partial<IListStringItem>): void {
    const newItem = this.createNewSerialItem(data)
    if (this.operatorType === EOperatorType.ADD) {
      newItem.id = 0
    }
    if (newItem.warnings && newItem.warnings.length > 0) {
      const messages = newItem.warnings.map((warning) => `[${warning}]: ${this.$t(`errors.${warning}`)}`)
      const message = messages.join('<br>')
      openMessage(message, 'warning', 5000, {
        dangerouslyUseHTMLString: true,
      })
      logError('', {}, '', message)
    }

    this.removeFromDeletedSerialData(newItem)
    this.localValue = [...this.localValue, newItem]
    this.scrollToBottom()
  }

  private validateCodePrefix(code: string): boolean {
    if (this.localValue.length === 0) {
      return code.startsWith(ALLOWED_SERIAL_PREFIX.TP) || code.startsWith(ALLOWED_SERIAL_PREFIX.BT)
    }

    const firstCodePrefix = this.localValue[0]?.code?.substring(0, 2)
    const newCodePrefix = code.substring(0, 2)

    return firstCodePrefix === newCodePrefix
  }

  get producedLastTrollyIndex(): number {
    return this.barcode?.serialSetting.producedLastTrollyIndex
  }

  get producedLastTrollyNumber(): number {
    return this.barcode?.serialSetting.producedLastTrollyNumber
  }

  lastTrollyNumber(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyNumber ?? 0
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyNumber ?? 0
      default:
        return 0
    }
  }

  lastTrollyIndex(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyIndex
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyIndex
      default:
        return this.barcode?.serialSetting.correctValueLastTrollyIndex
    }
  }

  handleGetTrollyInfo(operatorType: EOperatorType) {
    if (this.localValue.length === 0) {
      return {
        trolleyNumber: this.lastTrollyNumber(operatorType),
        trolleyIndex: this.lastTrollyIndex(operatorType),
      }
    }

    const lastItem = this.localValue[this.localValue.length - 1]
    const currentTrolleyIndex = lastItem.trollyIndex || this.lastTrollyIndex(operatorType) || 1

    const newTrolleyIndex = currentTrolleyIndex + 1
    const newTrolleyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.localValue))

    return {
      trolleyNumber: operatorType === EOperatorType.CORRECT_VALUE ? 0 : newTrolleyNumber,
      trolleyIndex: newTrolleyIndex,
    }
  }

  createNewSerialItem(item: Partial<IListStringItem>): IListStringItem {
    if (!this.operatorType) {
      throw new Error('Operator type is required to create a new item.')
    }
    const { trolleyNumber, trolleyIndex } = this.handleGetTrollyInfo(this.operatorType)

    return {
      ...item,
      itemCode: item.itemCode ?? '',
      code: item.code || '',
      limitAmount: item.limitAmount ?? this.defaultLimitAmount,
      amount: item.initAmount ?? this.defaultAmount,
      currentAmount: item.currentAmount ?? this.defaultAmount,
      unit: item.unit ?? this.defaultUnit,
      scannedDateTime: item.scannedDateTime ?? new Date().toISOString(),
      warnings: item.warnings ?? [],
      trollyNumber: trolleyNumber,
      trollyIndex: trolleyIndex,
    } as IListStringItem
  }

  onConfirmListSerialCodes() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.scannedDialogVisible = true
  }

  onCloseScannedDialog() {
    this.scannedDialogVisible = false
    this.checkScannedBarcode = false
  }

  get isKeyenceEnterpriseBrowser(): boolean {
    return isKeyenceEnterpriseBrowser()
  }

  isItemEditable(index: number): boolean {
    return this.isOperatorTypeCollect || (!this.isOperatorTypeUse && index === this.listValue.length - 1)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

.custom-barcode-number-block {
  margin-bottom: 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: $dark-grey;
  color: #fff;
  border: none;
}

.custom-barcode-number-block:active {
  transform: translateY(1px);
}

.input-label {
  cursor: pointer;
  display: block;
  margin-bottom: 6px;
  font-size: $main-fsize;
  font-weight: 500;
  color: #fff;
  transition: color 0.2s ease;
}

.fake-input {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  min-height: 44px;
  box-sizing: border-box;
  color: white;
}

.fake-input:focus-within {
  border-color: #409eff;
  outline: none;
}

.input-value {
  flex: 1;
  color: white;
  font-size: $main-fsize;
  text-align: center;
}

.input-value-add {
  flex: 1;
  color: white;
  font-size: $main-fsize;
  text-align: center;
  background: $dark-grey;
}

.fake-input .input-value:empty::before {
  content: 'Click to view items';
  color: #999;
}

.list-items {
  max-height: 300px;
  overflow-y: auto;
}

.list-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.list-item:last-child {
  border-bottom: none;
}

.list-container {
  height: 35vh;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.barcode-item-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.barcode-item {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 7px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;

  .item-name {
    font-weight: 400;
    color: #666;
    text-align: left;
    word-break: break-word;
    font-size: $main-fsize;
    line-height: 1.3;
    margin-top: 8px;
    padding-top: 8px;
    padding-left: 6px;
    padding-right: 6px;
    width: 100%;
    order: 10;
    border-top: 2px solid #dee2e6;
  }

  .item-text {
    padding-top: 5px;
    flex: 1;
    border-right: 2px solid #dee2e6;
    margin-right: 8px;

    .item-code {
      font-weight: 600;
      color: #333;
      word-break: break-all;
      font-size: $main-fsize;
      text-align: left;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
    }
  }

  .amount-input-wrapper {
    display: flex;
    align-items: center;
    font-size: $main-fsize;
    margin-left: auto;
    position: relative;
    width: 105px;
    padding-left: 8px;

    .amount-input {
      flex: 1;

      ::v-deep .el-input__inner {
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        text-align: center;
        font-size: $main-fsize;
        height: 32px;
        background-color: white;
        padding: 0px;
        padding-right: 50%;
        padding-left: 2px;
      }
    }

    .unit-suffix {
      position: absolute;
      right: 8px;
      font-size: 12px;
      color: #666;
      font-weight: 500;
      background-color: #f5f5f5;
      padding: 2px 6px;
      border-radius: 3px;
      border: 1px solid #e9ecef;
      pointer-events: none;
      z-index: 1;
    }
  }

  .item-actions {
    margin-left: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .trolley-index {
    font-size: $main-fsize;
    align-content: center;
    color: #666;
    background-color: #e9ecef;
    padding: 4px 8px;
    border-radius: 8px;
    white-space: nowrap;
    border: 1px solid grey;
    font-weight: 600;
    text-align: center;
  }

  .trolley-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-right: 8px;
    margin-right: 8px;
    border-right: 2px solid #dee2e6;
  }

  .trolley-number {
    justify-content: center;
    font-size: 20px;
    padding: 4px 8px;
    border-radius: 8px;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid #333;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background-color: #f3f4f6;
    color: #333;
    box-shadow: 0 1px 3px rgba(64, 158, 255, 0.2);

    .trolley-icon {
      width: $main-fsize;
      height: $main-fsize;
      flex-shrink: 0;
    }
  }

  .delete-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50% !important;
    font-size: 16px;
    font-weight: bold;
    color: white !important;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    min-width: 32px !important;
    border: none !important;

    // Mobile responsive - larger touch target
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      min-width: 36px !important;
    }

    &:hover {
      opacity: 0.8;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.3);
    }
  }

  .move-btn {
    min-width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    // Mobile responsive - larger buttons on mobile
    @media (max-width: 768px) {
      min-width: 36px;
      height: 36px;
      font-size: $main-fsize;
    }
  }
}

.manual-input-section {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #f8f9fa;

  .input-row {
    display: flex;
    gap: 12px;
    align-items: center;

    .manual-input {
      flex: 1;
    }

    .add-btn {
      min-width: 60px;
      height: 40px;
    }
  }
}

.summary-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  color: #333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .summary-item {
    display: flex;
    font-size: $main-fsize;
    flex-direction: column;
    gap: 6px;

    &:last-child {
      align-items: flex-end;
    }

    .summary-label {
      // font-size: 12px;
      font-weight: 500;
      color: #666;
      text-transform: uppercase;
      text-align: left;
      letter-spacing: 0.5px;
    }

    .summary-value {
      // font-size: 15px;
      font-weight: 600;
      letter-spacing: 0.3px;
      color: #333;
    }

    .summary-count {
      // font-size: 15px;
      font-weight: 600;
      white-space: nowrap;
      color: #333;
      text-align: left;
    }
  }

  .summary-divider {
    width: 1px;
    height: 44px;
    background-color: #dee2e6;
    margin: 0 $main-fsize;
  }
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  .cancel-btn,
  .confirm-btn {
    flex: 1;
    height: 64px;
    font-weight: 500;
  }

  .cancel-btn {
    background-color: white;
    border-color: #6c757d;
    color: #6c757d;
  }

  .confirm-btn {
    background-color: $dark-grey;
    border-color: $dark-grey;
    color: white;
  }
}

.empty-state {
  text-align: center;
  color: #999;
}

.dialog-footer {
  text-align: right;
}

.edit-view {
  padding: 0 16px 16px 16px;
}

.error-msg {
  color: #f56c6c;
  font-size: $main-fsize;
  line-height: 1.2;
  margin-top: 4px;
  display: block;
}

.display-in-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

::v-deep .el-dialog__header {
  display: block !important;
  padding: 20px 20px 0px;
}

.screen-name-label {
  font-size: 25px;
  font-weight: bold;
  color: #303133;
}

::v-deep .el-dialog__body {
  height: 100%;
  padding: 0px;
  // margin: 0 20px 30px 20px;
}

// Scan Mode Styles
.scan-mode-buttons {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 15px;

  .scan-add-btn,
  .scan-remove-btn {
    flex: 1;
    height: 80px;
    // font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;
    margin: 0;
    position: relative;
    padding: 0px 10px;

    .button-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      position: relative;

      .button-icon {
        position: absolute;
        left: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 768px) {
          left: 16px;
        }
      }

      .button-text {
        flex: 1;
        text-align: center;
        font-size: $main-fsize;

        @media (max-width: 768px) {
          font-size: $main-fsize;
        }
      }
    }
  }
}

.scan-view {
  padding: 0 16px 16px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scanner-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.scanner-frame {
  position: relative;
  width: 280px;
  height: 280px;
  border: 2px dashed #409eff;
  border-radius: 12px;
  background: linear-gradient(45deg, rgba(64, 158, 255, 0.05) 0%, rgba(64, 158, 255, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 220px;
    height: 220px;
  }
}

.scanner-line {
  position: absolute;
  width: 80%;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #409eff 50%, transparent 100%);
  animation: scan-line 2s linear infinite;
}

@keyframes scan-line {
  0% {
    top: 20px;
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  100% {
    top: calc(100% - 20px);
    opacity: 0;
  }
}

.scanner-corners {
  position: absolute;
  width: 100%;
  height: 100%;

  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid #409eff;

    &.top-left {
      top: -2px;
      left: -2px;
      border-right: none;
      border-bottom: none;
    }

    &.top-right {
      top: -2px;
      right: -2px;
      border-left: none;
      border-bottom: none;
    }

    &.bottom-left {
      bottom: -2px;
      left: -2px;
      border-right: none;
      border-top: none;
    }

    &.bottom-right {
      bottom: -2px;
      right: -2px;
      border-left: none;
      border-top: none;
    }
  }
}

.scanner-info {
  text-align: center;

  .mode-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
    font-size: $main-fsize;
    margin-bottom: 12px;
    display: inline-flex;

    &.add {
      background-color: rgba(64, 158, 255, 0.1);
      color: #409eff;
    }

    &.remove {
      background-color: rgba(245, 108, 108, 0.1);
      color: #f56c6c;
    }
  }

  .scan-instructions {
    color: #666;
    font-size: $main-fsize;
    margin: 0;
  }
}

.scanned-preview {
  margin: 16px 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h4 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: $main-fsize;
  }

  .preview-list {
    max-height: 120px;
    overflow-y: auto;

    .preview-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background-color: white;
      border-radius: 6px;
      margin-bottom: 8px;
      border: 1px solid #e9ecef;

      .item-value {
        flex: 1;
        font-size: $main-fsize;
        color: #333;
      }

      .remove-preview-btn {
        color: #f56c6c;
        font-size: 18px;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: rgba(245, 108, 108, 0.1);
        }
      }
    }
  }
}

.manual-scan-input {
  margin: 16px 0;
  padding: 16px;
  background-color: #f0f7ff;
  border-radius: 8px;
  border: 1px solid #b3d8ff;

  .input-row {
    display: flex;
    gap: 12px;
    align-items: center;

    .manual-input {
      flex: 1;
    }

    .simulate-btn {
      min-width: 100px;
      height: 40px;
    }
  }
}

// Camera scanner styles (from PairScan component)

.step-label {
  margin-bottom: 25px;
  // font-size: 18px;
  font-weight: 600;
  color: #333;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.barcode-number-block {
  font-size: $main-fsize;
  display: flex;
  align-items: center;
  flex-direction: row;
  text-align: center;
  justify-content: center;

  span {
    margin: 0 4px;
  }
}

.new-scanned-block {
  .new-scanned-count {
    transition: all 0.2s ease;
  }

  &.has-items {
    font-weight: 700;
    color: $error;

    .new-scanned-count {
      background-color: $error;
      border-color: $error;
      color: $pure-white;
      animation: new-scanned-pulse 1.6s ease-in-out infinite;
    }
  }
}

@keyframes new-scanned-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.55);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(245, 108, 108, 0);
  }
}

.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  padding: 8px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
}

.scan-dialog-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  .cancel-btn,
  .apply-btn {
    flex: 1;
    height: 44px;
    font-weight: 500;
  }

  .cancel-btn {
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
  }

  .apply-btn {
    background-color: #409eff;
    border-color: #409eff;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
